import { isValid } from 'iban-ts'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { z } from 'zod'

import { Locale } from '@/constants/locales'
import { getCountryNameByISO3 } from '@/lib/country'

import { EntityType } from '../types'

const MAX_ADDRESS_LENGTH = 105

export const recipientValidation = z
  .object({
    entityType: z.nativeEnum(EntityType),
    nickname: z.string().min(1, {
      message: 'validation.nickname.required',
    }),

    // Business fields
    legalName: z.string().optional(),

    // Individual fields
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    secondLastName: z.string().optional(),

    email: z
      .string()
      .email({
        message: 'validation.email.invalid',
      })
      .optional()
      .or(z.literal('')),
    internationalPhonePrefix: z
      .string()
      .min(1, {
        message: 'validation.phoneCode.required',
      })
      .optional()
      .or(z.literal('')),
    localPhoneNumber: z
      .string()
      .min(1, {
        message: 'validation.phoneNumber.required',
      })
      .refine(isValidPhoneNumber, {
        message: 'validation.phoneNumber.invalid',
      })
      .optional()
      .or(z.literal('')),
  })
  .superRefine((data, ctx) => {
    if (data.entityType === EntityType.INDIVIDUAL) {
      if (!data.firstName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.firstName.required',
          path: ['firstName'],
        })
      }

      if (!data.lastName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.lastName.required',
          path: ['lastName'],
        })
      }
    }

    if (data.entityType === EntityType.BUSINESS) {
      if (!data.legalName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'validation.legalName.required',
          path: ['legalName'],
        })
      }
    }
  })

export const clabeValidation = z.object({
  clabe: z
    .string()
    .min(17, {
      message: 'validation.clabe.invalid',
    })
    .refine((v) => /^\d+$/.test(v), {
      message: 'validation.clabe.invalid',
    }),
})

export const ibanValidation = z.object({
  iban: z
    .string()
    .min(1, {
      message: 'validation.iban.required',
    })
    .refine((v) => isValid(v), {
      message: 'validation.iban.invalid',
    }),
})

export const paymentValidation = z.object({
  paymentMethod: z
    .string()
    .min(1, { message: 'validation.paymentMethod.required' }),
  accountNumber: z
    .string()
    .min(1, { message: 'validation.accountNumber.required' }),
  routingNumber: z
    .string()
    .min(1, { message: 'validation.routingNumber.required' }),
  address: z
    .object({
      street: z
        .string()
        .min(3, { message: 'validation.addressStreet.required' })
        .max(35, { message: 'validation.addressStreet.maxLength' })
        .refine((v) => /\d/.test(v), {
          message: 'validation.addressStreet.streetNumberRequired',
        })
        .refine(
          (v) => {
            return !/P\.?\s*O\.?\s*BOX/i.test(v)
          },
          {
            message: 'validation.addressStreet.poBoxNotAllowed',
          },
        )
        .refine((v) => !/PMB/i.test(v), {
          message: 'validation.addressStreet.pmbNotAllowed',
        }),
      city: z.string().min(1, { message: 'validation.addressCity.required' }),
      countryCode: z
        .string()
        .min(1, { message: 'validation.addressCountry.required' }),
      state: z.string().min(1, { message: 'validation.addressState.required' }),
      postcode: z
        .string()
        .min(1, { message: 'validation.addressZipCode.required' }),
    })
    .refine(
      (data) => {
        const country = getCountryNameByISO3(data.countryCode, Locale.EN)

        if (country && data.street.includes(country)) {
          return false
        }

        if (data.state !== '' && data.street.includes(data.state)) {
          return false
        }

        if (data.postcode !== '' && data.street.includes(data.postcode)) {
          return false
        }

        if (data.city !== '' && data.street.includes(data.city)) {
          return false
        }

        return true
      },
      {
        message: 'validation.addressStreet.onlyNameAndNumber',
        path: ['street'],
      },
    )
    .refine(
      (data) => {
        const totalAddressLength =
          data.street.length +
          data.city.length +
          data.state.length +
          data.postcode.length

        return totalAddressLength < MAX_ADDRESS_LENGTH
      },
      {
        message: 'validation.addressStreet.addressTooLong',
        path: ['street'],
      },
    ),
})
